import type { Reaction } from '@ms/yammer-graphql';
import { isJwtToken } from '@ms/yammer-web-authenticators/dist/jwt';
import type { GetAuthToken } from '@ms/yammer-web-authenticators/dist/mobileEmbedView';

import type {
  AuthCallback,
  Context,
  ContextCallback,
  GetAuthTokenResponse,
  MessageRequest,
  PageLoadPerfMetrics,
  PageTitleDetails,
  TokenResponse,
} from './mobileEmbedView.types';

let nextMessageId = 0;
const callbacks: Record<number, AuthCallback | ContextCallback> = {};

const createMessageRequest = (messageRequest: Omit<MessageRequest, 'id'>): MessageRequest => ({
  id: nextMessageId++,
  ...messageRequest,
});

const sendMessageRequestToParent = (request: MessageRequest) => {
  // oxlint-disable-next-line typescript/no-explicit-any
  (window as any).ReactNativeWebView.postMessage(JSON.stringify(request));
};

const invokeAuthCallback = ({ id, tokenResponses }: GetAuthTokenResponse) => {
  const callback = callbacks[id];

  if (callback) {
    const authCallback = callback as AuthCallback;
    authCallback.apply(null, [tokenResponses]);
    // oxlint-disable-next-line typescript/no-dynamic-delete
    delete callbacks[id];
  }
};

const initializeVivaConnections = () => {
  // oxlint-disable-next-line typescript/no-explicit-any
  (window as any).invokeAuthCallback = invokeAuthCallback;
};

export type EnsureVivaConnectionsInitialized = () => void;
export const ensureVivaConnectionsInitialized: EnsureVivaConnectionsInitialized = () => {
  initializeVivaConnections();
};

export const getAuthToken: GetAuthToken = (authTokenRequest) => {
  const request = createMessageRequest({ func: 'GetAuthToken', args: [authTokenRequest.resource] });

  callbacks[request.id] = (tokenResponses: TokenResponse[]) => {
    if (!tokenResponses || tokenResponses.length === 0) {
      authTokenRequest.failureCallback('No token response received.');

      return;
    }

    const { success, token, error } = tokenResponses[0];

    if (success) {
      if (!token) {
        authTokenRequest.failureCallback('getAuthToken succeeded but no token received.');
      } else if (!isJwtToken(token)) {
        authTokenRequest.failureCallback('getAuthToken succeeded but invalid token received.');
      } else {
        authTokenRequest.successCallback(token);
      }
    } else {
      const errorMessage = error || 'Failed with no error.';
      authTokenRequest.failureCallback(errorMessage);
    }
  };

  sendMessageRequestToParent(request);
};

type SendPageLoadPerfMetrics = (pageLoadMetrics: PageLoadPerfMetrics) => void;
export const sendPageLoadPerfMetrics: SendPageLoadPerfMetrics = (pageLoadMetrics) => {
  const request = createMessageRequest({ func: 'yammerPageLoadPerfMetrics', data: pageLoadMetrics });
  sendMessageRequestToParent(request);
};

type SetYammerPageTitle = (pageTitleDetails: PageTitleDetails) => void;
export const setYammerPageTitle: SetYammerPageTitle = (pageTitleDetails) => {
  const request = createMessageRequest({ func: 'setYammerPageTitle', data: pageTitleDetails });
  sendMessageRequestToParent(request);
};

export type GetVivaConnectionsContext = () => Context;
// oxlint-disable-next-line typescript/no-explicit-any
export const getVivaConnectionsContext: GetVivaConnectionsContext = () => (window as any).appContext as Context;

type AddReaction = (messageId: string, reaction: Reaction) => void;
export const addReaction: AddReaction = (messageId, reaction) => {
  const request = createMessageRequest({
    func: 'yammerSocialReactionSync',
    data: {
      eventName: 'addReaction',
      messageId,
      reaction,
    },
  });

  sendMessageRequestToParent(request);
};

type RemoveReaction = (messageId: string) => void;
export const removeReaction: RemoveReaction = (messageId) => {
  const request = createMessageRequest({
    func: 'yammerSocialReactionSync',
    data: {
      eventName: 'removeReaction',
      messageId,
    },
  });

  sendMessageRequestToParent(request);
};

type GetCachedItem = (cacheKey: string) => string | undefined;
export const getCachedItem: GetCachedItem = (cacheKey) => {
  // oxlint-disable-next-line typescript/no-explicit-any
  const cachedData: Record<string, string> | undefined = (window as any).cachedData;

  return cachedData && cachedData[cacheKey];
};

type SetCachedItem = (cacheKey: string, cacheValue: string) => void;
export const setCachedItem: SetCachedItem = (cacheKey: string, cacheValue: string) => {
  const request = createMessageRequest({
    func: 'setCachedItem',
    data: {
      cacheKey,
      cacheValue,
    },
  });

  sendMessageRequestToParent(request);
};

type RemoveCachedItem = (cacheKey: string) => void;
export const removeCachedItem: RemoveCachedItem = (cacheKey) => {
  const request = createMessageRequest({
    func: 'removeCachedItem',
    data: {
      cacheKey,
    },
  });

  sendMessageRequestToParent(request);
};

type GetVivaConnectionsTheme = () => string | undefined;

export const getVivaConnectionsAppTheme: GetVivaConnectionsTheme = () => {
  const context = getVivaConnectionsContext();

  if (!context) {
    return undefined;
  }

  return context.theme;
};
